import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ReportCard } from '../ReportCard';
import type { PerformanceReport, TrendPoint } from './data/api';
import { compactCurrency, formatKpi } from './data/format';

const VIEWBOX_WIDTH = 1240;
const PLOT_HEIGHT = 210;
const PADDING_LEFT = 74;
const PADDING_RIGHT = 74;
const PADDING_TOP = 20;
const GRIDLINES = 4;
const SPEND_HEADROOM = 1.15;
const GRIDLINE_STEPS = [0, 1, 2, 3, 4];
const KPI_HEADROOM = 1.25;

interface TrendChartProps {
  report: PerformanceReport;
}

function Legend({
  spendColor,
  kpiColor,
  kpiLabel,
}: {
  spendColor: string;
  kpiColor: string;
  kpiLabel: string;
}): ReactNode {
  return (
    <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {[
        { color: spendColor, label: 'Spend' },
        { color: kpiColor, label: kpiLabel },
      ].map((entry) => (
        <Box key={entry.label} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: entry.color }} />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{entry.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export function TrendChart({ report }: TrendChartProps): ReactNode {
  const theme = useTheme();
  const points: TrendPoint[] = report.trend;

  if (points.length === 0) {
    return (
      <ReportCard label="Period over period">
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          No delivery in this period.
        </Typography>
      </ReportCard>
    );
  }

  const spendColor = theme.palette.primary.main;
  const kpiColor = theme.palette.secondary.main;
  const spendCeiling = Math.max(...points.map((point) => point.spend), 1) * SPEND_HEADROOM;
  const kpiCeiling =
    Math.max(...points.map((point) => point.kpiValue ?? 0), Number.EPSILON) * KPI_HEADROOM;
  const plotWidth = VIEWBOX_WIDTH - PADDING_LEFT - PADDING_RIGHT;
  const bandWidth = plotWidth / points.length;
  const centreX = (index: number): number => PADDING_LEFT + bandWidth * index + bandWidth / 2;
  const kpiY = (value: number): number =>
    PADDING_TOP + PLOT_HEIGHT - (value / kpiCeiling) * PLOT_HEIGHT;

  // A bucket with no KPI is missing data, not a collapse to zero — it leaves a gap in the line
  // rather than a dive to the baseline.
  const plotted = points
    .map((point, index) => ({ point, index }))
    .filter((entry) => entry.point.kpiValue !== null);
  const line = plotted
    .map((entry) => `${centreX(entry.index)},${kpiY(entry.point.kpiValue ?? 0)}`)
    .join(' ');

  return (
    <ReportCard
      label="Period over period"
      action={<Legend spendColor={spendColor} kpiColor={kpiColor} kpiLabel={report.kpi.label} />}
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="svg"
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${PLOT_HEIGHT + 46}`}
          role="img"
          aria-label={`Spend and ${report.kpi.label} by bucket`}
          sx={{ display: 'block', width: '100%', minWidth: 480 }}
        >
          {GRIDLINE_STEPS.map((step) => {
            const y = PADDING_TOP + PLOT_HEIGHT - (step / GRIDLINES) * PLOT_HEIGHT;
            return (
              <g key={`gridline-${step}`}>
                <line
                  x1={PADDING_LEFT}
                  x2={PADDING_LEFT + plotWidth}
                  y1={y}
                  y2={y}
                  stroke={theme.palette.divider}
                  strokeWidth={1}
                />
                <text
                  x={PADDING_LEFT - 12}
                  y={y + 4}
                  textAnchor="end"
                  fontSize={9}
                  fill={theme.palette.text.secondary}
                >
                  {step === 0
                    ? '0'
                    : compactCurrency((spendCeiling * step) / GRIDLINES, report.currencySymbol)}
                </text>
                <text x={PADDING_LEFT + plotWidth + 12} y={y + 4} fontSize={9} fill={kpiColor}>
                  {step === 0
                    ? '0'
                    : formatKpi((kpiCeiling * step) / GRIDLINES, report.kpi, report.currencySymbol)}
                </text>
              </g>
            );
          })}

          {points.map((point, index) => {
            const barWidth = Math.min(bandWidth * 0.5, 140);
            const barHeight = (point.spend / spendCeiling) * PLOT_HEIGHT;
            return (
              <g key={point.date}>
                <rect
                  x={centreX(index) - barWidth / 2}
                  y={PADDING_TOP + PLOT_HEIGHT - barHeight}
                  width={barWidth}
                  height={barHeight}
                  rx={6}
                  fill={spendColor}
                  opacity={0.18}
                />
                <text
                  x={centreX(index)}
                  y={PADDING_TOP + PLOT_HEIGHT - barHeight + 18}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill={spendColor}
                >
                  {compactCurrency(point.spend, report.currencySymbol)}
                </text>
              </g>
            );
          })}

          <polyline
            points={line}
            fill="none"
            stroke={kpiColor}
            strokeWidth={2.5}
            strokeLinejoin="round"
          />
          {plotted.map((entry) => (
            <circle
              key={entry.point.date}
              cx={centreX(entry.index)}
              cy={kpiY(entry.point.kpiValue ?? 0)}
              r={5.5}
              fill={kpiColor}
              stroke={theme.palette.background.paper}
              strokeWidth={2}
            />
          ))}

          {points.map((point, index) => (
            <text
              key={point.date}
              x={centreX(index)}
              y={PLOT_HEIGHT + 38}
              textAnchor="middle"
              fontSize={11}
              fill={theme.palette.text.secondary}
            >
              {point.date}
            </text>
          ))}
        </Box>
      </Box>
    </ReportCard>
  );
}
